'use client';

import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { cn, hasValue } from '@woonstad/helpers-utils';

import { Typography } from '../../typography/Typography';
import { useFormField } from '../FormField';

type Props<T = string> = {
  label: string;
  sublabel?: string;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  onCheckedChange?: CheckboxPrimitive.CheckboxProps['onCheckedChange'];
  transformValueOnChange?: (value: CheckboxPrimitive.CheckedState) => T;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  id?: string;
  testid?: string;
};

export function Checkbox<T = string>({
  label,
  sublabel,
  onClick,
  disabled,
  onCheckedChange,
  value,
  checked,
  id,
  transformValueOnChange,
  testid,
}: Props<T>) {
  const {
    field,
    formItemId,
  } = useFormField();

  const isChecked = hasValue(checked) ? checked : field?.value;
  const isDisabled = disabled || field?.disabled;

  const inputClasses = cn(
    'flex size-5 shrink-0 items-center justify-center border-2 outline-offset-4',
    {
      'border-checkbox-enabled hover:border-checkbox-hover': !isDisabled,
      'border-checkbox-disabled': isDisabled,
    },
  );
  const fieldId = id ?? formItemId;

  return (
    <div
      className={cn(
        'group flex w-fit text-checkbox-enabled',
        !isDisabled && 'hover:text-checkbox-hover',
        isDisabled && 'opacity-50',
      )}
      data-testid={testid}
    >
      <CheckboxPrimitive.Root
        onClick={onClick}
        aria-label={label}
        checked={isChecked}
        name={field?.name}
        className={inputClasses}
        disabled={isDisabled}
        id={fieldId}
        onBlur={field?.onBlur}
        value={value}
        onCheckedChange={(state) => {
          const transformedValue = hasValue(transformValueOnChange) ? transformValueOnChange(state) : state;

          if (onCheckedChange) {
            onCheckedChange(state);
          }

          field?.onChange(transformedValue);
        }}
      >
        <CheckboxPrimitive.Indicator
          className={'size-3 bg-checkbox-enabled-toggle'}
        />
      </CheckboxPrimitive.Root>

      <label
        className={cn('-mt-px pl-2', {
          'cursor-pointer': !isDisabled,
        })}
        htmlFor={fieldId}
      >
        <Typography
          data-disabled={isDisabled}
          type="span"
          variant="content-body"
        >
          <span
            className={cn('block text-checkbox-enabled', !isDisabled && 'group-hover:text-checkbox-hover')}
          >
            {label}
          </span>
        </Typography>

        {sublabel && (
          <Typography
            data-disabled={isDisabled}
            textColorClassname="text-input-hint"
            type="span"
            variant="label-small"
          >
            <span className={cn('block', !isDisabled && 'cursor-pointer')}>
              {sublabel}
            </span>
          </Typography>
        )}
      </label>
    </div>
  );
}
